import React, { useState, useMemo } from 'react';
import { AlertTriangle, Users, DollarSign, Clock, TrendingUp, Phone, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { OverdueTable } from './OverdueTable';
import { OverdueFilters } from './OverdueFilters';
import { OverdueActions } from './OverdueActions';
import { BatchActionsDropdown } from '../transactions/BatchActionsDropdown';
import { BatchDatePickerModal } from '../transactions/BatchDatePickerModal';
import { BatchAmountModal } from '../transactions/BatchAmountModal';
import { BatchAccountSelectModal } from '../transactions/BatchAccountSelectModal';
import {
  OverdueGroup,
  OverdueTransaction,
  OverdueClient,
  OverdueFilters as OverdueFiltersType,
  OverdueSummary,
  OverdueStatus,
  OverduePriority,
  RiskLevel
} from '../../types/overdue';
import { useOverdueAccounts } from '@/hooks/useOverdueAccounts';
import { toast } from 'sonner';


export function OverduePage() {
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());

  // Batch operation modals state
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isAmountModalOpen, setIsAmountModalOpen] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

  // Initialize filters
  const [filters, setFilters] = useState<OverdueFiltersType>({
    status: Object.values(OverdueStatus),
    priority: Object.values(OverduePriority),
    sort_by: 'days_overdue',
    sort_direction: 'desc'
  });

  // Use real data from hook
  const { data: filteredData, summary, isLoading, error, refetch } = useOverdueAccounts(filters);

  // Handle error
  React.useEffect(() => {
    if (error) {
      console.error('Error loading overdue data:', error);
      toast.error('Erro ao carregar dados de inadimplência');
    }
  }, [error]);

  // Selection handlers
  const handleSelectTransaction = (transactionId: string, selected: boolean) => {
    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(transactionId);
      } else {
        newSet.delete(transactionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allTransactionIds = filteredData.flatMap(group => 
        group.transactions.map(t => t.id)
      );
      setSelectedTransactions(new Set(allTransactionIds));
    } else {
      setSelectedTransactions(new Set());
    }
  };

  const handleActionComplete = () => {
    setSelectedTransactions(new Set());
    // Refetch real data
    refetch();
    toast.info('Dados atualizados');
  };

  const handleContactClient = (clientId: string, method: 'email' | 'phone' | 'whatsapp') => {
    const client = filteredData.find(g => g.client.id === clientId)?.client;
    if (!client) return;

    switch (method) {
      case 'email':
        toast.success(`Abrindo email para ${client.name}`);
        break;
      case 'phone':
        toast.success(`Iniciando ligação para ${client.name}`);
        break;
      case 'whatsapp':
        toast.success(`Abrindo WhatsApp para ${client.name}`);
        break;
    }
  };

  const handlePriorityChange = (transactionId: string, priority: OverduePriority) => {
    // In a real implementation, this would call an API to update the priority
    console.log('Updating priority for transaction:', transactionId, 'to:', priority);
    refetch();
  };

  const handleStatusChange = (transactionId: string, status: OverdueStatus) => {
    // In a real implementation, this would call an API to update the status
    console.log('Updating status for transaction:', transactionId, 'to:', status);
    refetch();
  };

  const handleSendNotice = (clientId: string, method: 'email' | 'phone' | 'whatsapp', transactionIds?: string[]) => {
    const client = filteredData.find(g => g.client.id === clientId)?.client;
    if (!client) return;

    const transactionCount = transactionIds?.length || 0;
    const message = transactionCount > 0
      ? `Enviando notificação via ${method} para ${client.name} (${transactionCount} transação(ões))`
      : `Enviando notificação via ${method} para ${client.name}`;

    toast.success(message);
  };

  const handleAddNotes = (transactionId: string, notes: string) => {
    // In a real implementation, this would call an API to add notes
    console.log('Adding notes to transaction:', transactionId, 'notes:', notes);
    refetch();
  };

  const handleMarkResolved = (transactionId: string) => {
    // In a real implementation, this would call an API to mark as resolved
    console.log('Marking transaction as resolved:', transactionId);
    refetch();
  };

  const handleScheduleFollowUp = (transactionId: string, date: Date) => {
    // In a real implementation, this would call an API to schedule follow-up
    console.log('Scheduling follow-up for transaction:', transactionId, 'date:', date);
    toast.success(`Follow-up agendado para ${date.toLocaleDateString('pt-BR')}`);
  };

  // Calculate selected totals
  const selectedTotals = useMemo(() => {
    if (!filteredData || !filteredData.length) return { totalAmount: 0, clientCount: 0 };

    const selectedTransactionsList = filteredData.flatMap(group =>
      group.transactions.filter(t => selectedTransactions.has(t.id))
    );

    const totalAmount = selectedTransactionsList.reduce((sum, t) => sum + t.current_amount, 0);
    const clientIds = new Set(selectedTransactionsList.map(t => t.client_id));

    return {
      totalAmount,
      clientCount: clientIds.size
    };
  }, [filteredData, selectedTransactions]);

  return (
    <div className="section-delimited space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Inadimplentes</h1>
        <p className="text-muted-foreground">
          Gerencie e recupere lançamentos em atraso de forma eficiente
        </p>
      </div>

      {/* High Priority Alert */}
      {summary.by_priority[OverduePriority.CRITICAL]?.count > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Atenção:</strong> {summary.by_priority[OverduePriority.CRITICAL].count} lançamento(s)
            com prioridade crítica requerem ação imediata.
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="content-section">
        <OverdueFilters
        filters={filters}
        onFiltersChange={setFilters}
        summary={summary}
        loading={isLoading}
        onRefresh={() => refetch()}
      />
      </div>

      {/* Actions Bar */}
      <div className="content-section">
        <OverdueActions
        selectedTransactions={selectedTransactions}
        onActionComplete={handleActionComplete}
        totalAmount={selectedTotals.totalAmount}
        clientCount={selectedTotals.clientCount}
      />
        {/* Batch Actions */}
        {selectedTransactions.size > 0 && (
          <div className="mt-4">
            <BatchActionsDropdown
              selectedTransactions={selectedTransactions}
              onClearSelection={() => setSelectedTransactions(new Set())}
              onOpenDateModal={() => setIsDateModalOpen(true)}
              onOpenAmountModal={() => setIsAmountModalOpen(true)}
              onOpenAccountModal={() => setIsAccountModalOpen(true)}
            />
          </div>
        )}
      </div>

      {/* Main Table */}
      <div className="content-section">
        <OverdueTable
          data={filteredData}
          onSelectTransaction={handleSelectTransaction}
          onSelectAll={handleSelectAll}
          selectedTransactions={selectedTransactions}
          onContactClient={handleContactClient}
          onPriorityChange={handlePriorityChange}
          onStatusChange={handleStatusChange}
          onSendNotice={handleSendNotice}
          onAddNotes={handleAddNotes}
          onMarkResolved={handleMarkResolved}
          onScheduleFollowUp={handleScheduleFollowUp}
          loading={isLoading}
          sortable={true}
        />
      </div>

      {/* Empty State */}
      {!isLoading && filteredData.length === 0 && (
        <div className="content-section">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Nenhuma transação em atraso encontrada
              </h3>
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                Não há receitas vencidas no momento.
                Quando houver transações em atraso, elas aparecerão aqui.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Batch Operation Modals */}
      <BatchDatePickerModal
        open={isDateModalOpen}
        onOpenChange={setIsDateModalOpen}
        selectedTransactions={selectedTransactions}
        onClearSelection={() => setSelectedTransactions(new Set())}
      />
      <BatchAmountModal
        open={isAmountModalOpen}
        onOpenChange={setIsAmountModalOpen}
        selectedTransactions={selectedTransactions}
        onClearSelection={() => setSelectedTransactions(new Set())}
      />
      <BatchAccountSelectModal
        open={isAccountModalOpen}
        onOpenChange={setIsAccountModalOpen}
        selectedTransactions={selectedTransactions}
        onClearSelection={() => setSelectedTransactions(new Set())}
      />
    </div>
  );
}